/**
 * Subscription & Pricing Types
 * Centralized definitions for the payment system
 */

import type { SubscriptionTier } from "@/db/schema";

// ============================================================================
// Plan Feature Interface
// ============================================================================

export interface PlanFeature {
  text: string;
  included: boolean;
  highlight?: boolean;
}

// ============================================================================
// Subscription Plan Interface
// ============================================================================

export interface SubscriptionPlan {
  id: SubscriptionTier;
  name: string;
  description: string;
  price: number; // Monthly price in USD (0 for free)
  yearlyPrice?: number; // Annual price if different
  creditsPerMonth: number;
  features: PlanFeature[];
  popular?: boolean;
  cta: string; // Call to action text
  stripePriceId?: string; // For Stripe integration
}

// ============================================================================
// Credit Package Interface (for one-time purchases)
// ============================================================================

export interface CreditPackage {
  id: string;
  credits: number;
  price: number;
  bonus?: number; // Bonus credits
  popular?: boolean;
  stripePriceId?: string;
}

// ============================================================================
// Subscription Plans Configuration
// ============================================================================

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: "free",
    name: "Free",
    description: "Perfect for trying out MerchMidas",
    price: 0,
    creditsPerMonth: 50,
    cta: "Get Started",
    features: [
      { text: "50 credits/month", included: true },
      { text: "All 6 AI tools", included: true },
      { text: "Watermarked exports", included: true },
      { text: "Email support", included: true },
      { text: "HD exports (720p)", included: true },
      { text: "Priority processing", included: false },
      { text: "API access", included: false },
      { text: "Team collaboration", included: false },
      { text: "White-label options", included: false },
      { text: "Custom integrations", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    id: "starter",
    name: "Starter",
    description: "For creators just getting serious",
    price: 9,
    yearlyPrice: 84, // $7/mo × 12
    creditsPerMonth: 250,
    cta: "Start Free Trial",
    features: [
      { text: "250 credits/month", included: true, highlight: true },
      { text: "All 6 AI tools", included: true },
      { text: "No watermarks", included: true, highlight: true },
      { text: "Email support", included: true },
      { text: "HD exports (720p)", included: true },
      { text: "Priority processing", included: false },
      { text: "API access", included: false },
      { text: "Team collaboration", included: false },
      { text: "White-label options", included: false },
      { text: "Custom integrations", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    description: "For professionals and content creators",
    price: 29,
    yearlyPrice: 276, // $23/mo × 12
    creditsPerMonth: 1000,
    cta: "Start Pro Trial",
    popular: true,
    features: [
      { text: "1,000 credits/month", included: true, highlight: true },
      { text: "All 6 AI tools", included: true },
      { text: "No watermarks", included: true, highlight: true },
      { text: "Priority support", included: true },
      { text: "Full HD exports (1080p)", included: true },
      { text: "Priority processing", included: true, highlight: true },
      { text: "API access", included: false },
      { text: "Team collaboration", included: false },
      { text: "White-label options", included: false },
      { text: "Custom integrations", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    id: "business",
    name: "Business",
    description: "For teams and growing businesses",
    price: 99,
    yearlyPrice: 948, // $79/mo × 12
    creditsPerMonth: 5000,
    cta: "Start Business Trial",
    features: [
      { text: "5,000 credits/month", included: true, highlight: true },
      { text: "All 6 AI tools", included: true },
      { text: "No watermarks", included: true },
      { text: "Priority support", included: true },
      { text: "4K exports", included: true, highlight: true },
      { text: "Priority processing", included: true },
      { text: "API access", included: true, highlight: true },
      { text: "Up to 5 team members", included: true, highlight: true },
      { text: "White-label options", included: false },
      { text: "Custom integrations", included: false },
      { text: "SLA guarantee", included: false },
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    description: "Custom solutions for large organizations",
    price: -1, // Custom pricing
    creditsPerMonth: -1, // Unlimited
    cta: "Contact Sales",
    features: [
      { text: "Unlimited credits", included: true, highlight: true },
      { text: "All 6 AI tools", included: true },
      { text: "No watermarks", included: true },
      { text: "Dedicated support", included: true, highlight: true },
      { text: "4K+ exports", included: true },
      { text: "Fastest processing", included: true },
      { text: "Full API access", included: true },
      { text: "Unlimited team members", included: true, highlight: true },
      { text: "White-label options", included: true, highlight: true },
      { text: "Custom integrations", included: true },
      { text: "SLA guarantee", included: true },
    ],
  },
];

// ============================================================================
// Credit Packages Configuration
// ============================================================================

export const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: "credits-100",
    credits: 100,
    price: 9,
  },
  {
    id: "credits-500",
    credits: 500,
    price: 39,
    bonus: 50, // 550 total
    popular: true,
  },
  {
    id: "credits-1000",
    credits: 1000,
    price: 69,
    bonus: 150, // 1150 total
  },
  {
    id: "credits-5000",
    credits: 5000,
    price: 299,
    bonus: 1000, // 6000 total
  },
];

// ============================================================================
// Credit Costs per Tool
// ============================================================================

export const CREDIT_COSTS: Record<string, number> = {
  "try-on": 2,
  staging: 1,
  ads: 3,
  designer: 1,
  visualizer: 1,
  unboxing: 2,
};

// ============================================================================
// Utility Functions
// ============================================================================

export function getPlanById(id: string): SubscriptionPlan | undefined {
  return SUBSCRIPTION_PLANS.find((plan) => plan.id === id);
}

export function getFreePlan(): SubscriptionPlan {
  return SUBSCRIPTION_PLANS.find((plan) => plan.id === "free")!;
}

export function formatPrice(price: number): string {
  if (price === 0) return "Free";
  if (price === -1) return "Custom";
  return `$${price}`;
}

export function formatCredits(credits: number): string {
  if (credits === -1) return "Unlimited";
  return credits.toLocaleString();
}

export function canUpgrade(
  currentTier: SubscriptionTier,
  targetTier: SubscriptionTier
): boolean {
  const tierOrder: SubscriptionTier[] = ["free", "pro", "business", "enterprise"];
  return tierOrder.indexOf(targetTier) > tierOrder.indexOf(currentTier);
}

export function canDowngrade(
  currentTier: SubscriptionTier,
  targetTier: SubscriptionTier
): boolean {
  const tierOrder: SubscriptionTier[] = ["free", "pro", "business", "enterprise"];
  return tierOrder.indexOf(targetTier) < tierOrder.indexOf(currentTier);
}
